import React, { useContext, useEffect, useState } from 'react'
import {
  Box,
  Button, Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  Input,
  InputLabel, MenuItem,
  Paper, Select,
  Switch,
}                                                 from '@mui/material'
import { useTranslation }                         from 'react-i18next'
import { useNavigate, useParams }                 from 'react-router-dom'
import { useApi }                                 from '../utils/api'
import { useSnackbar }                            from 'notistack'
import AlertSimpleDialog                          from '../ui/components/alert-simple-dialog'
import Page                                       from '../ui/components/Page'
import { BaseBreadcrumbs }                        from '../ui/components/BaseBreadcrumbs'
import { AppContext }                             from '../App'
import Consts                                     from '../Consts'

export default function UserPage() {

  const api = useApi()
  const navigate = useNavigate()
  const { userId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const [values, setValues] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    type: '',
    partnerId: '',
    readonly: false
  })
  const [showPasswordPanel, setShowPasswordPanel] = useState(isNaN(userId))
  const [shownDeleteDialog, setShownDeleteDialog] = useState(false)
  const [partners, setPartners] = useState([])
  const { user } = useContext(AppContext)
  const isAdmin = user.type === Consts.User.typeAdmin

  useEffect(() => {
    (async () => {
      if (!isNaN(userId)) {
        try{
          let userDetails = await api.user.detail(userId)
          setValues({
            name: '',
            email: '',
            password: '',
            confirmPassword: '',
            cityId: '',
            type: '',
            partnerId: '',
            readonly: false,
            ...userDetails
          })
        } catch (e){
          enqueueSnackbar(t('Can\'t load user'), {
            variant: "error"
          })
        }
      }
    })()
  }, [userId])

  useEffect(() => {
    if (isAdmin) {
      api.partner.list(null, 0, 1000).then(({ data }) => setPartners(data))
    }
  }, [isAdmin])

  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value })
  }
  const handleChangeDirect = (prop, value) => {
    setValues((prev) => ({
      ...prev,
      [prop]: value
    }))
  }

  const handleCloseDeleteDialog = (result) => {
    setShownDeleteDialog(false)
    if (result) {
      api.user.delete(userId).then(() => {
        enqueueSnackbar(t('User deleted'), {
          variant: 'success'
        })
        navigate('/users')
      }).catch(error => {
        enqueueSnackbar(t('Failed to delete user'), {
          variant: 'error'
        })
      })
    }
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    let data = { ...values }
    if (!showPasswordPanel) {
      // if the password is not changed, ignore it
      delete data.password
      delete data.confirmPassword
    }
    if (values.type === Consts.User.typeAdmin && !values.readonly) {
      delete data.partnerId
    }
    try {
      let updatedUser = await api.user.save(data)
      navigate(`/users/${updatedUser.id}`)
      enqueueSnackbar(t('User saved'), {
        variant: 'success'
      })
    } catch (e) {
      enqueueSnackbar(t('Failed to save user'), {
        variant: 'error'
      })
    }
  }

  return (
    <Page title={t('User')}>
      <Container component="main">
        <BaseBreadcrumbs key={'breadcrumb-user'}
                         title={values?.name ?? t('User')}
                         items={[
                           { title: t('Users'), href: `/users` }
                         ]}/>
        <Grid component={'form'} onSubmit={onSubmit} item xs={12}>
          <Paper sx={{ p: 2, width: '100%' }}>
            <Grid container sx={{ width: '100%' }} spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth variant="standard">
                  <InputLabel htmlFor="user-name">{t('Name')}</InputLabel>
                  <Input
                    id="user-name"
                    value={values.name}
                    onChange={handleChange('name')}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth variant="standard">
                  <InputLabel htmlFor="user-email">{'Email'}</InputLabel>
                  <Input
                    id="user-email"
                    value={values.email}
                    disabled={!isNaN(userId)}
                    onChange={handleChange('email')}
                  />
                </FormControl>
              </Grid>
              {isAdmin && <>
              <Grid item xs={12}>
                <FormControl variant="standard" fullWidth>
                  <InputLabel id="type-label">{t('Type')}</InputLabel>
                  <Select
                    labelId="type-label"
                    id="type-select"
                    value={values?.type ?? ''}
                    label={t('Type')}
                    onChange={handleChange('type')}
                  >
                    <MenuItem key={'type-admin'} value={Consts.User.typeAdmin}>{t('user-type-admin')}</MenuItem>
                    <MenuItem key={'type-partner'} value={Consts.User.typePartner}>{t('user-type-partner')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                {values.type === Consts.User.typeAdmin && (
                  <FormControlLabel control={<Checkbox />} checked={values.readonly} onChange={(e) => {
                    handleChangeDirect('readonly', e.target.checked)
                  }} label={t('Readonly')} />
                )}
              </Grid>
              <Grid item xs={12}>
                {partners?.length > 0 && (values.type === Consts.User.typePartner || values.type === Consts.User.typeAdmin && values.readonly) && <>
                  <FormControl variant="standard" fullWidth>
                    <InputLabel id="partner-select-label">{t('Partner')}</InputLabel>
                    <Select
                      labelId="partner-select-label"
                      id="partner-select"
                      value={values?.partnerId ?? ''}
                      label={t('Partner')}
                      onChange={handleChange('partnerId')}
                    >
                      <MenuItem key={'partner-not-set'} value={''}>{t('Not set')}</MenuItem>
                      { partners?.map(partner => (<MenuItem key={`partner-${partner.id}`} value={partner.id}>{partner.name}</MenuItem>)) }
                    </Select>
                  </FormControl>
                </>}
              </Grid>
              </>}
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch
                    checked={showPasswordPanel}
                    onChange={(e) => setShowPasswordPanel(!showPasswordPanel)}
                    disabled={isNaN(userId)}
                  />}
                  label={t('Set password')} />
              </Grid>
              { showPasswordPanel && <>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="standard">
                    <InputLabel htmlFor="user-password">{t('Password')}</InputLabel>
                    <Input
                      id="user-password"
                      type={'password'}
                      value={values.password}
                      onChange={handleChange('password')}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="standard">
                    <InputLabel htmlFor="user-password-confirm">{t('Confirm password')}</InputLabel>
                    <Input
                      id="user-password-confirm"
                      type={'password'}
                      value={values.confirmPassword}
                      onChange={handleChange('confirmPassword')}
                    />
                  </FormControl>
                </Grid>
              </>}
            </Grid>
          </Paper>
          <Box sx={{ mt: 2, px: 1 }}>
            <Button type={'submit'} variant={'contained'} disabled={user?.readonly}>{t('Save')}</Button>
            { !isNaN(userId) && isAdmin && (<Button type={'button'} color={'error'} variant={'contained'} sx={{ ml: 2 }} disabled={user?.readonly} onClick={() => { setShownDeleteDialog(true) }}>{t('Delete')}</Button>) }
          </Box>
        </Grid>
        <AlertSimpleDialog
          title={t('Deleting user')}
          message={t('delete-confirm-msg', { name: values.name })}
          open={shownDeleteDialog}
          onClose={handleCloseDeleteDialog}
        />
      </Container>
    </Page>
  )
}